import type { ChangeEvent } from "react";
import type { ThemePreference } from "../../api/types.js";
import {
  type Appearance,
  type AppearanceBorderColor,
  type AppearanceCardSize,
  type AppearanceContrast,
  type AppearanceDensity,
  type AppearanceFont,
  type AppearanceMotion,
  type AppearanceMotionFeel,
  type AppearanceTextSize,
  type AppearanceTickerCount,
  type AppearanceTickerSpeed,
  DEFAULT_APPEARANCE,
  useAppearance,
} from "../../app/appearance.js";
import "./appearance-panel.css";

const OPTIONS = {
  font: [
    { value: "default", label: "Skin default" },
    { value: "legible", label: "High-legibility" },
    { value: "system", label: "System" },
  ] as const,
  textSize: [
    { value: "sm", label: "Small" },
    { value: "md", label: "Medium" },
    { value: "lg", label: "Large" },
    { value: "xl", label: "X-Large" },
  ] as const,
  density: [
    { value: "comfortable", label: "Comfortable" },
    { value: "compact", label: "Compact" },
  ] as const,
  motion: [
    { value: "auto", label: "Auto (follow OS)" },
    { value: "full", label: "Full" },
    { value: "reduced", label: "Reduced" },
  ] as const,
  contrast: [
    { value: "standard", label: "Standard" },
    { value: "high", label: "High" },
  ] as const,
  motionFeel: [
    { value: "smooth", label: "Smooth" },
    { value: "spring", label: "Spring" },
    { value: "sharp", label: "Sharp" },
    { value: "off", label: "Off" },
  ] as const,
  cardSize: [
    { value: "compact", label: "Compact" },
    { value: "normal", label: "Normal" },
    { value: "large", label: "Large" },
  ] as const,
  tickerSpeed: [
    { value: "slow", label: "Slow" },
    { value: "medium", label: "Medium" },
    { value: "fast", label: "Fast" },
  ] as const,
  tickerCount: [
    { value: "few", label: "Few" },
    { value: "normal", label: "Normal" },
    { value: "many", label: "Many" },
  ] as const,
} satisfies Record<string, readonly { value: string; label: string }[]>;

const SKINS: readonly { value: ThemePreference; label: string }[] = [
  { value: "vantablack", label: "Vantablack" },
  { value: "light", label: "Light" },
];

const BORDER_COLORS: readonly {
  value: AppearanceBorderColor;
  label: string;
}[] = [
  { value: "default", label: "Skin default" },
  { value: "none", label: "No color" },
  { value: "lavender", label: "Lavender" },
  { value: "salmon", label: "Salmon" },
  { value: "red", label: "Red" },
  { value: "earth", label: "Titian earth" },
  { value: "gold", label: "Gold" },
  { value: "teal", label: "Teal" },
];

function RadioGroup<T extends string>({
  legend,
  name,
  value,
  options,
  onChange,
}: {
  legend: string;
  name: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="appearance-panel__fieldset">
      <legend>{legend}</legend>
      <div className="appearance-panel__options">
        {options.map((option) => (
          <label key={option.value}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                onChange(event.target.value as T)
              }
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function AppearancePanel({
  theme,
  onThemeChange,
}: {
  theme: ThemePreference;
  onThemeChange: (theme: ThemePreference) => void;
}) {
  const { appearance, setAppearance } = useAppearance();
  const patch = (next: Partial<Appearance>) =>
    setAppearance({ ...appearance, ...next });
  const reset = () => {
    setAppearance(DEFAULT_APPEARANCE);
    onThemeChange("vantablack");
  };

  return (
    <section
      className="appearance-panel"
      data-testid="appearance-panel"
      aria-labelledby="appearance-heading"
    >
      <div>
        <h2 id="appearance-heading">Appearance</h2>
        <p>
          Choose a skin and tune reading comfort. Saved on this device only.
        </p>
      </div>
      <div className="appearance-panel__grid">
        <RadioGroup
          legend="Skin"
          name="skin"
          value={theme}
          options={SKINS}
          onChange={onThemeChange}
        />
        <RadioGroup<AppearanceFont>
          legend="Reading font"
          name="font"
          value={appearance.font}
          options={OPTIONS.font}
          onChange={(font) => patch({ font })}
        />
        <RadioGroup<AppearanceTextSize>
          legend="Text size"
          name="textSize"
          value={appearance.textSize}
          options={OPTIONS.textSize}
          onChange={(textSize) => patch({ textSize })}
        />
        <RadioGroup<AppearanceDensity>
          legend="Density"
          name="density"
          value={appearance.density}
          options={OPTIONS.density}
          onChange={(density) => patch({ density })}
        />
        <RadioGroup<AppearanceMotion>
          legend="Motion"
          name="motion"
          value={appearance.motion}
          options={OPTIONS.motion}
          onChange={(motion) => patch({ motion })}
        />
        <RadioGroup<AppearanceContrast>
          legend="Contrast"
          name="contrast"
          value={appearance.contrast}
          options={OPTIONS.contrast}
          onChange={(contrast) => patch({ contrast })}
        />
        <RadioGroup<AppearanceMotionFeel>
          legend="Motion feel"
          name="motionFeel"
          value={appearance.motionFeel}
          options={OPTIONS.motionFeel}
          onChange={(motionFeel) => patch({ motionFeel })}
        />
        <RadioGroup<AppearanceCardSize>
          legend="Card size"
          name="cardSize"
          value={appearance.cardSize}
          options={OPTIONS.cardSize}
          onChange={(cardSize) => patch({ cardSize })}
        />
        <RadioGroup<AppearanceTickerSpeed>
          legend="Ticker speed"
          name="tickerSpeed"
          value={appearance.tickerSpeed}
          options={OPTIONS.tickerSpeed}
          onChange={(tickerSpeed) => patch({ tickerSpeed })}
        />
        <RadioGroup<AppearanceTickerCount>
          legend="Ticker count"
          name="tickerCount"
          value={appearance.tickerCount}
          options={OPTIONS.tickerCount}
          onChange={(tickerCount) => patch({ tickerCount })}
        />
        <fieldset className="appearance-panel__fieldset appearance-panel__fieldset--wide">
          <legend>Border color</legend>
          <div className="appearance-panel__options appearance-panel__border-options">
            {BORDER_COLORS.map((option) => (
              <label key={option.value} data-border-option={option.value}>
                <input
                  type="radio"
                  name="borderColor"
                  value={option.value}
                  checked={appearance.borderColor === option.value}
                  onChange={() => patch({ borderColor: option.value })}
                />
                <span className="appearance-panel__swatch" aria-hidden="true" />
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <button
        className="button appearance-panel__reset"
        type="button"
        onClick={reset}
      >
        Reset to defaults
      </button>
      <p aria-live="polite">Appearance preferences apply immediately.</p>
    </section>
  );
}
